import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminService } from '../../services/adminService';
import MeetbotPlatforms from './MeetbotPlatforms';

const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [models, setModels] = useState([]);
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  // Самостоятельная регистрация внешних клиентов — сохраняется сразу по клику,
  // а не по кнопке «Сохранить»: это выключатель, а не поле формы.
  const [selfSignup, setSelfSignup] = useState(false);
  const [llmFallback, setLlmFallback] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [data, modelsData] = await Promise.all([
        adminService.getSettings(),
        adminService.getLlmModels().catch(() => ({ models: [] })),
      ]);
      setSettings(data);
      setDraft({
        token_expiration_days: data.token_expiration_days,
        llm_api_endpoint: data.llm_api_endpoint || '',
        llm_api_key: data.llm_api_key || '',
        local_llm_model: data.local_llm_model || '',
        cloud_llm_model: data.cloud_llm_model || '',
        cloud_llm_context_tokens: data.cloud_llm_context_tokens ?? 200000,
        local_llm_context_tokens: data.local_llm_context_tokens ?? 40000,
      });
      setAdminEmails(data.admin_emails || []);
      setSelfSignup(!!data.self_signup_enabled);
      setLlmFallback(!!data.llm_fallback_enabled);
      setModels(modelsData.models || []);
    } catch (e) {
      setError('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const updates = {
        ...draft,
        token_expiration_days: isNaN(parseInt(draft.token_expiration_days)) ? 30 : parseInt(draft.token_expiration_days),
        cloud_llm_context_tokens: isNaN(parseInt(draft.cloud_llm_context_tokens)) ? 200000 : parseInt(draft.cloud_llm_context_tokens),
        local_llm_context_tokens: isNaN(parseInt(draft.local_llm_context_tokens)) ? 40000 : parseInt(draft.local_llm_context_tokens),
        admin_emails: adminEmails,
      };
      const result = await adminService.updateSettings(updates);
      setSettings(result);
      setDraft(prev => ({ ...prev, llm_api_key: result.llm_api_key || '' }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const addAdminEmail = () => {
    const e = newAdminEmail.trim().toLowerCase();
    if (!e || !e.includes('@') || adminEmails.includes(e)) return;
    setAdminEmails([...adminEmails, e]);
    setNewAdminEmail('');
  };

  const removeAdminEmail = (email) => {
    setAdminEmails(adminEmails.filter(e => e !== email));
  };

  const updateDraft = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  if (loading) return <div className="text-center py-8 text-paper-hint">Загрузка...</div>;

  return (
    <div className="mt-4 max-w-xl">
      {error && <p className="text-paper-accent text-sm mb-3">{error}</p>}
      {success && <p className="text-paper-success text-sm mb-3">Настройки сохранены</p>}

      {/* Авторизация */}
      <Section title="Авторизация">
        <Row label="Срок действия токена" hint="Через сколько дней токен истекает">
          <div className="flex items-center gap-2">
            <Input
              className="w-20 text-center"
              type="number"
              min={1}
              max={365}
              value={draft.token_expiration_days}
              onChange={(e) => updateDraft('token_expiration_days', e.target.value)}
            />
            <span className="text-xs text-paper-ink-3">дней</span>
          </div>
        </Row>
        <Row label="Администраторы" hint="Email получающие роль admin автоматически">
          <div>
            <div className="flex flex-wrap gap-1 mb-1 justify-end">
              {adminEmails.map(email => (
                <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 bg-paper-panel text-paper-accent rounded-chip text-xs">
                  {email}
                  <button className="hover:text-paper-accent-hover" onClick={() => removeAdminEmail(email)}>✕</button>
                </span>
              ))}
            </div>
            <Input
              className="w-52 text-xs"
              placeholder="email@domain.com + Enter"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addAdminEmail()}
            />
          </div>
        </Row>
        <Row
          label="Самостоятельная регистрация клиентов"
          hint="Включено — любой внешний адрес может завести аккаунт с тарифом «Оплата по факту» (100 минут)"
        >
          <button
            data-testid="toggle-self-signup"
            onClick={async () => {
              const next = !selfSignup;
              setSelfSignup(next);
              try {
                await adminService.updateSettings({ self_signup_enabled: next });
              } catch (e) {
                setSelfSignup(!next);
                setError('Не удалось изменить настройку регистрации');
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              selfSignup ? 'bg-paper-success' : 'bg-paper-track'
            }`}
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-paper-card transition-transform ${
              selfSignup ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </Row>
      </Section>

      {/* Суммаризация */}
      <Section title="Суммаризация">
        <Row label="API эндпоинт" hint="URL сервера LLM моделей">
          <Input
            className="w-60"
            value={draft.llm_api_endpoint}
            onChange={(e) => updateDraft('llm_api_endpoint', e.target.value)}
          />
        </Row>
        <Row label="API ключ" hint="Ключ авторизации для LLM API">
          <div className="flex items-center gap-1">
            <Input
              className="w-52"
              type={showApiKey ? 'text' : 'password'}
              value={draft.llm_api_key}
              onChange={(e) => updateDraft('llm_api_key', e.target.value)}
            />
            <Button variant="outline" size="sm" className="px-2" onClick={() => setShowApiKey(!showApiKey)}>
              {showApiKey ? '🙈' : '👁'}
            </Button>
          </div>
        </Row>
        <Row label="Локальная модель" hint="Основная: на ней считаются все протоколы">
          <ModelSelect
            value={draft.local_llm_model}
            models={models}
            onChange={(v) => updateDraft('local_llm_model', v)}
          />
        </Row>
        <Row label="Окно контекста (локальная)" hint="Токены; куски режутся по меньшему из двух окон">
          <Input
            data-testid="local-ctx"
            className="w-52"
            type="number"
            min={1000}
            value={draft.local_llm_context_tokens}
            onChange={(e) => updateDraft('local_llm_context_tokens', e.target.value)}
          />
        </Row>
        <Row label="Облачная модель" hint="Запасная: сюда уходит протокол, когда локальная модель перегружена">
          <ModelSelect
            value={draft.cloud_llm_model}
            models={models}
            onChange={(v) => updateDraft('cloud_llm_model', v)}
          />
        </Row>
        <Row
          label="Запасная модель при перегрузке"
          hint="Включено — если локальная модель не ответила за 45 секунд или отказала, протокол дочитывает «Облачная модель». Сначала убедитесь, что в поле выше стоит та модель, куда данные передавать можно"
        >
          <button
            data-testid="toggle-llm-fallback"
            onClick={async () => {
              const next = !llmFallback;
              setLlmFallback(next);
              try {
                await adminService.updateSettings({ llm_fallback_enabled: next });
              } catch (e) {
                setLlmFallback(!next);
                setError('Не удалось изменить настройку запасной модели');
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              llmFallback ? 'bg-paper-success' : 'bg-paper-track'
            }`}
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-paper-card transition-transform ${
              llmFallback ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </Row>
        <Row label="Окно контекста (облачная)" hint="Токены; куски режутся по меньшему из двух окон">
          <Input
            data-testid="cloud-ctx"
            className="w-52"
            type="number"
            min={1000}
            value={draft.cloud_llm_context_tokens}
            onChange={(e) => updateDraft('cloud_llm_context_tokens', e.target.value)}
          />
        </Row>
      </Section>

      {/* Площадки бота на встречах: тумблеры сохраняются сразу, как и выключатели выше. */}
      <Section title="Бот на встречах: площадки">
        <MeetbotPlatforms value={settings?.meetbot_platforms} loaded={!!settings} onError={setError} />
      </Section>

      <div className="flex justify-end pt-3 border-t border-paper-line">
        <Button
          className="bg-paper-accent hover:bg-paper-accent-hover"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-7">
    <h3 className="text-xs font-semibold text-paper-ink uppercase tracking-wider mb-1">{title}</h3>
    <div className="border-t border-paper-line pt-3 space-y-3">{children}</div>
  </div>
);

const Row = ({ label, hint, children }) => (
  <div className="flex justify-between items-start">
    <div>
      <div className="text-sm font-medium text-paper-ink-2">{label}</div>
      {hint && <div className="text-xs text-paper-hint">{hint}</div>}
    </div>
    {children}
  </div>
);

const ModelSelect = ({ value, models, onChange }) => (
  <select
    className="w-52 px-3 py-1.5 rounded-chip bg-paper-bg text-sm"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    {value && !models.includes(value) && <option value={value}>{value}</option>}
    {models.map(m => (
      <option key={m} value={m}>{m}</option>
    ))}
  </select>
);

export default SettingsTab;
